/**
 * Report status predicates — the single place that decides whether an AI
 * visibility report is still moving.
 *
 * The status column is a free-text string in Postgres (see `AiReport.status`
 * in schema.prisma), and the generator writes SEVEN values, not the five the
 * UI historically enumerated. `processing_discovery` in particular is the
 * longest phase of a run (discovery queries fanned out across every platform)
 * and it was missing from every in-flight check on the reports grid, so the
 * grid stopped polling exactly when it had the most to wait for.
 *
 * Kept free of React and of `@tanstack/react-query` so it can be unit tested
 * and imported from anywhere.
 */

/** Every status the report generator can persist. */
export const AI_REPORT_STATUSES = [
	"pending",
	"running",
	"partial",
	"processing_discovery",
	"complete",
	"failed",
	"cancelled",
] as const;

export type AiReportStatus = (typeof AI_REPORT_STATUSES)[number];

/**
 * The subset the API accepts on a write. `aiReportStatusSchema` in
 * `@repo/core` is still the five-value enum, so `processing_discovery` and
 * `cancelled` can be READ from a report but not sent back in a create or
 * patch body. Keeping the two types apart makes that asymmetry explicit
 * instead of failing at the Hono RPC boundary.
 */
export type AiReportWritableStatus = Exclude<
	AiReportStatus,
	"processing_discovery" | "cancelled"
>;

/**
 * Statuses a report never leaves. Anything else means the Inngest run is
 * still working and the row will change again without the user doing
 * anything.
 *
 * `partial` is NOT terminal: the generator sets it once the brand analysis
 * lands, then carries on into discovery.
 */
export const TERMINAL_AI_REPORT_STATUSES: readonly AiReportStatus[] = [
	"complete",
	"failed",
	"cancelled",
];

/**
 * True while the report is still being generated. An unrecognised status is
 * treated as in-flight on purpose: a new intermediate status added on the
 * server should make the UI keep watching, not make it stop.
 */
export function isReportInFlight(status: string | null | undefined): boolean {
	if (!status) return false;
	return !TERMINAL_AI_REPORT_STATUSES.includes(status as AiReportStatus);
}

/** The inverse, for readability at call sites. */
export function isReportTerminal(status: string | null | undefined): boolean {
	return !!status && !isReportInFlight(status);
}

/**
 * Whether the reports list should keep polling. Used as react-query's
 * `refetchInterval` predicate, so it must be cheap and pure.
 */
export function shouldPollReports(
	reports: ReadonlyArray<{ status: string }> | undefined | null,
): boolean {
	if (!reports || reports.length === 0) return false;
	return reports.some((report) => isReportInFlight(report.status));
}

/**
 * Poll cadence for the reports grid, in ms. Matches the cadence the grid used
 * before this lived in react-query, so the API sees no extra load.
 */
export const AI_REPORT_POLL_INTERVAL_MS = 15_000;

/**
 * Ids of reports that were in flight on the previous render and are terminal
 * now. Drives the one-shot invalidation of everything downstream of a
 * finished report (onboarding status, dashboards, competitors).
 */
export function findNewlyTerminalReportIds(
	previousInFlightIds: ReadonlySet<string>,
	reports: ReadonlyArray<{ id: string; status: string }> | undefined | null,
): string[] {
	if (!reports) return [];
	return reports
		.filter(
			(report) =>
				previousInFlightIds.has(report.id) &&
				isReportTerminal(report.status),
		)
		.map((report) => report.id);
}
